if (!customElements.get('deferred-rich-media')) {
  customElements.define(
    'deferred-rich-media',
    class DeferredRichMedia extends HTMLElement {
      connectedCallback() {
        const template = this.querySelector('template');
        const target = this.querySelector('.deferred-rich-media__content');
        if (!template || !target) return;

        this.firstFramePosters = [];
        const content = template.content.cloneNode(true);
        const fallbackPoster = this.getAttribute?.('data-poster');
        const posterRules = this.parsePosterRules();

        content.querySelectorAll('h1').forEach((heading) => {
          const replacement = document.createElement('h2');

          for (const attribute of heading.attributes) {
            replacement.setAttribute(attribute.name, attribute.value);
          }
          replacement.innerHTML = heading.innerHTML;
          heading.replaceWith(replacement);
        });

        content.querySelectorAll('img').forEach((image) => {
          const source = image.getAttribute('src');

          if (source) {
            image.setAttribute('src', this.optimizeImageUrl(source));
          }
          image.setAttribute('loading', 'lazy');
          image.setAttribute('decoding', 'async');
        });

        content.querySelectorAll('video').forEach((video) => {
          const poster = video.getAttribute('poster') || this.findPosterRule(video, posterRules) || fallbackPoster;
          const button = document.createElement('button');

          video.autoplay = false;
          video.controls = true;
          video.preload = 'metadata';
          video.removeAttribute('autoplay');
          video.setAttribute('playsinline', '');

          if (video.getAttribute('src')) {
            video.setAttribute('src', this.cleanVideoUrl(video.getAttribute('src')));
          }

          video.querySelectorAll('source').forEach((source) => {
            source.setAttribute('src', this.cleanVideoUrl(source.getAttribute('src')));
          });

          button.className = 'deferred-rich-media__poster';
          button.type = 'button';
          button.setAttribute('aria-label', 'Play video');
          if (poster) {
            button.setAttribute('style', `background-image: url("${poster}")`);
          } else {
            const firstFrameUrl = this.getVideoSource(video);

            if (firstFrameUrl) {
              button.classList.add('deferred-rich-media__poster--first-frame');
              button.firstFrameVideo = video;
              this.firstFramePosters.push(button);
            }
          }
          button.innerHTML = '<span class="deferred-rich-media__play" aria-hidden="true"></span>';
          button.addEventListener(
            'click',
            () => {
              this.releaseFirstFrame(button);
              button.replaceWith(video);
              video.load();
              video.play().catch(() => {});
            },
            { once: true },
          );

          video.replaceWith(button);
        });

        target.append(content);
        template.remove();
        this.observeFirstFramePosters();
      }

      disconnectedCallback() {
        this.firstFrameObserver?.disconnect();
        this.firstFramePosters?.forEach((button) => this.releaseFirstFrame(button));
      }

      cleanVideoUrl(videoUrl) {
        return videoUrl ? videoUrl.split(/[?#]/)[0] : '';
      }

      parsePosterRules() {
        const rules = this.getAttribute?.('data-poster-rules');
        if (!rules) return [];

        try {
          const parsedRules = JSON.parse(rules);
          if (!Array.isArray(parsedRules)) return [];

          return parsedRules.filter((rule) => rule?.match && rule?.poster);
        } catch {
          return [];
        }
      }

      findPosterRule(video, posterRules) {
        if (!posterRules.length) return null;

        const sources = [];
        const videoSource = video.getAttribute('src');
        if (videoSource) sources.push(videoSource);

        video.querySelectorAll('source').forEach((source) => {
          const sourceUrl = source.getAttribute('src');
          if (sourceUrl) sources.push(sourceUrl);
        });

        const matchingRule = posterRules.find((rule) =>
          sources.some((source) => source.includes(rule.match) || this.cleanVideoUrl(source).includes(rule.match)),
        );

        return matchingRule?.poster || null;
      }

      getVideoSource(video) {
        const videoSource = video.getAttribute('src');
        if (videoSource) return this.cleanVideoUrl(videoSource);

        for (const source of video.querySelectorAll('source')) {
          const sourceUrl = source.getAttribute('src');
          if (sourceUrl) return this.cleanVideoUrl(sourceUrl);
        }

        return '';
      }

      observeFirstFramePosters() {
        if (!this.firstFramePosters?.length) return;

        if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
          this.firstFramePosters.forEach((button) => this.loadFirstFrame(button));
          return;
        }

        this.firstFrameObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              this.loadFirstFrame(entry.target);
              this.firstFrameObserver?.unobserve(entry.target);
            });
          },
          { rootMargin: '200px 0px', threshold: 0.01 },
        );

        this.firstFramePosters.forEach((button) => this.firstFrameObserver.observe(button));
      }

      loadFirstFrame(button) {
        const preview = button.firstFrameVideo;
        if (!preview || button.dataset.firstFrameLoaded === 'true') return;

        button.dataset.firstFrameLoaded = 'true';
        button.firstFrameOriginalMuted = preview.muted;
        button.firstFrameOriginalControls = preview.controls;

        preview.classList.add('deferred-rich-media__first-frame');
        preview.muted = true;
        preview.controls = false;
        preview.playsInline = true;
        preview.preload = 'metadata';
        preview.setAttribute('aria-hidden', 'true');
        preview.setAttribute('tabindex', '-1');
        preview.addEventListener(
          'loadedmetadata',
          () => {
            if (preview.videoWidth > 0 && preview.videoHeight > 0) {
              button.style.aspectRatio = `${preview.videoWidth} / ${preview.videoHeight}`;
            }
            if (Number.isFinite(preview.duration) && preview.duration > 0) {
              preview.currentTime = Math.min(0.01, preview.duration / 2);
            }
          },
          { once: true },
        );
        preview.addEventListener('seeked', () => preview.pause(), { once: true });

        button.firstFramePreview = preview;
        button.prepend(preview);
        preview.load();
      }

      releaseFirstFrame(button) {
        this.firstFrameObserver?.unobserve(button);
        const preview = button.firstFramePreview;
        if (!preview) return;

        preview.pause();
        preview.classList.remove('deferred-rich-media__first-frame');
        preview.muted = button.firstFrameOriginalMuted;
        preview.controls = button.firstFrameOriginalControls;
        preview.removeAttribute('aria-hidden');
        preview.removeAttribute('tabindex');
        preview.remove();
        button.firstFramePreview = null;
      }

      optimizeImageUrl(imageUrl) {
        if (!imageUrl || !imageUrl.includes('cdn.shopify.com/s/files/')) return imageUrl;

        const url = new URL(imageUrl, 'https://luredolls.com');
        if (!url.searchParams.has('width')) {
          url.searchParams.set('width', '1200');
        }
        return url.toString();
      }
    },
  );
}
